import { ApiError } from '@passport/api-types';
import { enableConsoleLogs } from './debugFlags';

// Generic request/response logging below (post/handleResponse) is part of the login/sign-in
// flow diagnostics — gated behind enableConsoleLogs.loginProcess. DO NOT remove these
// console.log statements in future changes; toggle the flag instead. See debugFlags.ts.
function loginFlowLog(...args: unknown[]): void {
  if (enableConsoleLogs.loginProcess) {
    console.log(...args);
  }
}

export interface RequestConfig {
  headers?: Record<string, string>;
  params?: Record<string, any>;
  timeout?: number;
  skipRetry?: boolean;
}

export interface TokenRefreshCallback {
  (refreshToken: string): Promise<{ accessToken: string; refreshToken: string }>;
}

export class HttpClient {
  private baseURL: string;
  private token: string | null = null;
  private refreshToken: string | null = null;
  private refreshCallback: TokenRefreshCallback | null = null;
  private isRefreshing = false;

  constructor(baseURL: string) {
    // baseURL may be a relative path (e.g. '/', the deployment convention for same-origin
    // API calls) rather than an absolute origin. The URL constructor's `base` argument
    // requires an absolute URL, so resolve against window.location.origin up front — this
    // is a no-op when baseURL is already absolute (the first arg wins over base in that case).
    this.baseURL = typeof window !== 'undefined'
      ? new URL(baseURL, window.location.origin).toString()
      : baseURL;
  }

  setToken(token: string) {
    this.token = token;
  }

  clearToken() {
    this.token = null;
  }

  setRefreshToken(refreshToken: string) {
    this.refreshToken = refreshToken;
  }

  setRefreshCallback(callback: TokenRefreshCallback) {
    this.refreshCallback = callback;
  }

  private getSignal(config?: RequestConfig): AbortSignal {
    return AbortSignal.timeout(config?.timeout ?? 30_000);
  }

  private getHeaders(config?: RequestConfig): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...config?.headers,
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    return headers;
  }

  private buildURL(path: string, params?: Record<string, any>): string {
    const url = new URL(path, this.baseURL);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    }
    return url.toString();
  }

  async get<T>(path: string, config?: RequestConfig): Promise<T> {
    const response = await fetch(this.buildURL(path, config?.params), {
      method: 'GET',
      headers: this.getHeaders(config),
      signal: this.getSignal(config),
    });

    return this.handleResponse<T>(response, 'GET', path, undefined, config);
  }

  async post<T>(path: string, data?: any, config?: RequestConfig): Promise<T> {
    const url = this.buildURL(path, config?.params);
    // Only the auth/iam-related calls are logged here (not every POST this shared client makes
    // app-wide, e.g. billing/vault calls) to keep this focused on the login/sign-in flow per the
    // enableConsoleLogs.loginProcess contract.
    const isAuthFlowCall = path.includes('/auth/') || path.includes('/iam/');
    if (isAuthFlowCall) {
      loginFlowLog('[HttpClient.post] Outgoing request', {
        url,
        hasAuthHeader: !!this.token,
        bodyKeys: data ? Object.keys(data) : [],
      });
    }
    const response = await fetch(url, {
      method: 'POST',
      headers: this.getHeaders(config),
      body: data ? JSON.stringify(data) : undefined,
      signal: this.getSignal(config),
    });
    if (isAuthFlowCall) {
      loginFlowLog('[HttpClient.post] Response status received', { url, status: response.status, ok: response.ok });
    }

    return this.handleResponse<T>(response, 'POST', path, data, config);
  }

  async put<T>(path: string, data?: any, config?: RequestConfig): Promise<T> {
    const response = await fetch(this.buildURL(path, config?.params), {
      method: 'PUT',
      headers: this.getHeaders(config),
      body: data ? JSON.stringify(data) : undefined,
      signal: this.getSignal(config),
    });

    return this.handleResponse<T>(response, 'PUT', path, data, config);
  }

  // `data` is optional and defaults to no body, matching every existing caller (none of which
  // sent one). Added because BizFirst.IAM.Admin's DeleteWebRequest-based DELETE endpoints (e.g.
  // BaseResourceGrantController.Delete) bind `[FromBody] DeleteWebRequest` with no `id` method
  // parameter — the route's `{id:int}` segment is a URL-shape convention only, the record to
  // delete is identified entirely by the body's `id` (IDInfo). Without a body those deletes bind
  // an empty/zero IDInfo and fail service-side validation.
  async delete<T>(path: string, data?: any, config?: RequestConfig): Promise<T> {
    const response = await fetch(this.buildURL(path, config?.params), {
      method: 'DELETE',
      headers: this.getHeaders(config),
      body: data ? JSON.stringify(data) : undefined,
      signal: this.getSignal(config),
    });

    return this.handleResponse<T>(response, 'DELETE', path, data, config);
  }

  private async handleResponse<T>(
    response: Response,
    method: string,
    path: string,
    data?: any,
    config?: RequestConfig
  ): Promise<T> {
    const contentType = response.headers.get('content-type');
    // Matches "application/json" AND ASP.NET's RFC 7807 ProblemDetails error content type
    // "application/problem+json" (returned on 4xx/5xx by the default exception-handling
    // middleware) — the old exact "application/json" substring check missed the "+json" suffix
    // variant entirely, so every ProblemDetails error body fell through to the non-JSON branch
    // below and lost its `detail`/`title` message text.
    const isJson = contentType?.includes('json');

    // Handle 401 Unauthorized - try to refresh token and retry
    if (response.status === 401) {
      console.log('[HttpClient] 401 Unauthorized detected', {
        path,
        method,
        skipRetry: config?.skipRetry,
        hasRefreshToken: !!this.refreshToken,
        hasCallback: !!this.refreshCallback,
        isRefreshing: this.isRefreshing,
      });

      // Only attempt if: not already retrying, have a refresh token, have callback, and not already refreshing
      if (!config?.skipRetry && this.refreshToken && this.refreshCallback && !this.isRefreshing) {
        console.log('[HttpClient] Attempting automatic token refresh...');
        this.isRefreshing = true;
        try {
          const refreshResult = await this.refreshCallback(this.refreshToken);
          console.log('[HttpClient] Token refresh successful, retrying original request');
          this.setToken(refreshResult.accessToken);
          this.setRefreshToken(refreshResult.refreshToken);

          // Retry the original request with new token
          const retryConfig = { ...config, skipRetry: true };
          return this.retryRequest<T>(method, path, data, retryConfig);
        } catch (refreshError) {
          // If refresh fails, return the original 401 error
          console.error('[HttpClient] Token refresh failed:', refreshError);
          // Fall through to normal error handling
        } finally {
          this.isRefreshing = false;
        }
      } else {
        console.warn('[HttpClient] 401 detected but refresh cannot be attempted');
      }
    }

    const isAuthFlowCall = path.includes('/auth/') || path.includes('/iam/');

    if (!response.ok) {
      let error: ApiError;
      if (isJson) {
        const data = await response.json();
        if (isAuthFlowCall) {
          loginFlowLog('[HttpClient.handleResponse] Non-OK response body', {
            path,
            status: response.status,
            errors: data?.errors,
            message: data?.message,
          });
        }
        // Same GoWebResponse envelope the success path below unwraps ({success, data,
        // errors}) — the backend sends it on non-2xx responses too (e.g. a 401 login
        // failure carries errors[0].message: "Invalid credentials"), but this branch used
        // to only check data.message, which doesn't exist on this shape, and silently fell
        // back to response.statusText — dropping the actual reason and surfacing a bare
        // "Unauthorized" (or nothing) to the user instead of what the backend actually said.
        // Also falls back to `data.detail`/`data.title` — the fields ASP.NET's default
        // exception-handling middleware puts on an RFC 7807 ProblemDetails body (e.g. an
        // unhandled 500), which has neither `errors` nor `message`. Final `|| 'Request
        // failed'` guards against `response.statusText` being '' — Chrome leaves it empty on
        // HTTP/2 responses, which would otherwise produce a falsy, silently-swallowed message.
        error = {
          code: data.errors?.[0]?.code || data.code || 'UNKNOWN_ERROR',
          message:
            data.errors?.[0]?.message || data.message || data.detail || data.title || response.statusText || 'Request failed',
          statusCode: response.status,
        };
      } else {
        error = {
          code: 'UNKNOWN_ERROR',
          message: response.statusText || 'Request failed',
          statusCode: response.status,
        };
      }
      throw error;
    }

    if (!isJson) {
      return undefined as T;
    }

    const data_parsed = await response.json();

    if (isAuthFlowCall) {
      loginFlowLog('[HttpClient.handleResponse] OK response body received', {
        path,
        status: response.status,
        isEnvelope: !!(data_parsed && typeof data_parsed === 'object' && 'success' in data_parsed && 'data' in data_parsed),
        envelopeSuccess: data_parsed?.success,
      });
    }

    // Unwrap GoWebResponse envelope: { success, data, errors }
    if (data_parsed && typeof data_parsed === 'object' && 'success' in data_parsed && 'data' in data_parsed) {
      if (data_parsed.success === false) {
        const apiMsg = data_parsed.errors?.[0]?.message || data_parsed.message || 'Request failed';
        const apiCode = data_parsed.errors?.[0]?.code || 'API_ERROR';
        throw { code: apiCode, message: apiMsg, statusCode: response.status } as ApiError;
      }
      return data_parsed.data as T;
    }

    return data_parsed as T;
  }

  private async retryRequest<T>(method: string, path: string, data?: any, config?: RequestConfig): Promise<T> {
    const url = this.buildURL(path, config?.params);
    const options: RequestInit = {
      method,
      headers: this.getHeaders(config),
      signal: this.getSignal(config),
    };

    // DELETE is intentionally included here (see the `delete()` method above) — some backend
    // DELETE endpoints (e.g. BaseResourceGrantController.Delete) require a body.
    if (method !== 'GET' && data) {
      options.body = JSON.stringify(data);
    }

    const response = await fetch(url, options);
    return this.handleResponse<T>(response, method, path, data, config);
  }
}
